import React, { useContext, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import * as Yup from "yup";
import { Formik } from "formik";

import ImageUploader, {
  uploadBas64ToS3,
} from "../../components/utilities/ImageUploader";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";
import { getData, postData } from "../../components/utilities/UtilityFunctions";

export default function StoreAdminCategory() {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  let { categoryId } = useParams();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [category, setCategory] = useState(null);

  const [saveBoxRef, saveBoxInView] = useInView({
    threshold: 0,
  });

  useEffect(() => {
    const go = async () => {
      if (authContext?.authState?.identity?.isAdmin !== true) {
        navigate("/home");
        setLoading(false);
        return;
      }

      if (!categoryId) {
        setCategory({});
        setLoading(false);
        return;
      }
      try {
        var apiCategory = await getData(`/productcategories/${categoryId}`, {
          queryStringParameters: {},
        });
        setCategory(apiCategory);
      } catch (err) {
        postNotification("error", err);

        setLoading(false);
        return;
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [categoryId]);

  return (
    <>
      {!loading && (
        <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
          <Formik
            initialValues={{
              active: category?.active !== undefined ? category.active : false,
              name: category?.name || "",
              slug: category?.slug || "",
              description: category?.description || "",
            }}
            validationSchema={Yup.object().shape({
              name: Yup.string()
                .min(3, "Minimum 3 Characters!")
                .max(255, "Maximum 255 Characters!")
                .required("Required"),
              slug: Yup.string()
                .min(3, "Minimum 3 Characters!")
                .max(255, "Maximum 255 Characters!")
                .required("Required"),
            })}
            onSubmit={async (values) => {
              setProcessing(true);
              try {
                var body;
                var sanitizedValues = {
                  ...values,
                };

                if (sanitizedValues.imageFile?.imageURL) {
                  // upload image to s3
                  var newFile = await uploadBas64ToS3(
                    sanitizedValues.imageFile.imageURL
                  );
                  delete sanitizedValues.imageFile;
                  if (newFile.fileId) {
                    sanitizedValues.imageFileId = newFile.fileId;
                  }
                }

                if (category?.id) {
                  body = {
                    set: sanitizedValues,
                  };

                  await postData(`/productcategories/${category.id}`, {
                    body: body,
                  });
                  postNotification("success", "Category Updated");
                } else {
                  var apiData = await postData(`/productcategories`, {
                    body: sanitizedValues,
                  });
                  window.location.assign(`/admin/shop/category/${apiData.id}`);
                }
              } catch (err) {
                postNotification("error", err);
              }
              setProcessing(false);
              return;
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit} noValidate>
                <Grid container sx={{ gap: 2 }}>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Typography variant="h3">Category</Typography>
                    <Typography variant="h4">
                      {values?.name || "New Category"}
                    </Typography>
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      p: 2,
                      borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                      borderRadius: 1,
                      boxShadow: 1,
                      backgroundColor: "background.secondary",
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          label="Active"
                          sx={{
                            ml: 0,
                            width: "100%",
                            "& .MuiFormControlLabel-label": {
                              width: "calc(100% - 58px)",
                            },
                          }}
                          control={
                            <Switch
                              checked={values.active}
                              onChange={(e) => {
                                setFieldValue("active", !values.active);
                              }}
                            />
                          }
                          labelPlacement="start"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          name="name"
                          label="Category Name"
                          value={values.name}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={(e) => {
                            handleBlur(e);
                            if (values.slug === "") {
                              setFieldValue(
                                "slug",
                                e.target.value
                                  .split(" ")
                                  .join("-")
                                  .toLowerCase()
                              );
                            }
                          }}
                          onChange={handleChange}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="slug"
                          label="Slug"
                          value={values.slug}
                          variant="outlined"
                          fullWidth
                          required
                          margin="normal"
                          onBlur={(e) => {
                            if (e.target?.value?.length > 0) {
                              e.target.value = e.target.value
                                .split(" ")
                                .join("-")
                                .toLowerCase();
                            }
                            handleBlur(e);
                          }}
                          onChange={(e) => {
                            if (e.target?.value?.length > 0) {
                              e.target.value = e.target.value
                                .split(" ")
                                .join("-")
                                .toLowerCase();
                            }
                            handleChange(e);
                          }}
                          error={Boolean(touched.slug && errors.slug)}
                          helperText={touched.slug && errors.slug}
                          disabled={category?.id}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="body1" gutterBottom>
                          Image
                        </Typography>
                        <ImageUploader
                          values={values}
                          label={values.name}
                          name="imageFile.imageURL"
                          setFieldValue={setFieldValue}
                          fileId={category?.imageFileId}
                          defaultUrl={values.imageFile?.imageURL}
                          aspectW={1}
                          aspectY={1}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          name="description"
                          value={values.description}
                          label="Description"
                          variant="outlined"
                          fullWidth
                          onBlur={handleBlur}
                          onChange={handleChange}
                          multiline
                          rows={5}
                          error={Boolean(
                            touched.description && errors.description
                          )}
                          helperText={touched.description && errors.description}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          ref={saveBoxRef}
                          fullWidth
                          color="success"
                          variant="contained"
                          type="submit"
                        >
                          Save
                        </Button>
                      </Grid>

                      {!saveBoxInView && (
                        <Box
                          sx={{
                            position: "fixed",
                            zIndex: 999,
                            p: 2,
                            bgcolor: "background.paper",
                            borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                            boxShadow: 3,
                            bottom: 0,
                            left: [0, 0, 0, 256],
                            width: [
                              "100%",
                              "100%",
                              "100%",
                              "calc(100% - 256px)",
                            ],
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              fullWidth
                              color="success"
                              variant="contained"
                              type="submit"
                            >
                              Save
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Container>
      )}
      {(loading || processing) && <ModalLoadingFull />}
    </>
  );
}
